import { usePetitionActionsPageData, usePetitionActionsPageState } from '#src/app/pages/actions/pageContext';
import type { SupporterVoicesVideoFirstExperimentVariation } from '#src/app/pages/actions/pageContext';

import { useUploadStepContext } from './uploadStep';
import { useDisableVideoCreation } from './useDisableVideoCreation';
import { useUserRecordingCapability } from './useUserRecordingCapability';

/** R2's arms (CHANGE-99359). Unlike R1's `VideoFirstAskArm`, none of these have their own recorder flow. */
export type SupporterVoicesR2Arm = Extract<
	SupporterVoicesVideoFirstExperimentVariation,
	'v1_dm_impact' | 'v2_3x_impact' | 'v3_static_faces'
>;

/** The two arms that are copy-only diffs on today's invite - V3 gets a screen of its own. */
export type SupporterVoicesR2CopyArm = Exclude<SupporterVoicesR2Arm, 'v3_static_faces'>;

const SUPPORTER_VOICES_R2_ARMS: ReadonlySet<SupporterVoicesVideoFirstExperimentVariation> = new Set([
	'v1_dm_impact',
	'v2_3x_impact',
	'v3_static_faces',
]);

/**
 * R2's arm, or undefined for control / R1 / a viewer this experiment isn't relevant to.
 *
 * Same two gates as `useVideoFirstAskArm`: a petition with no video ask makes the experiment
 * irrelevant, and a viewer who cannot record would dead-end on a screen whose only path is recording.
 */
export function useSupporterVoicesR2Arm(): SupporterVoicesR2Arm | undefined {
	const {
		data: { supporterVoicesVideoFirstExperiment },
	} = usePetitionActionsPageData();
	const disableVideoCreation = useDisableVideoCreation();
	const capability = useUserRecordingCapability();

	const { variation } = supporterVoicesVideoFirstExperiment;

	if (disableVideoCreation || capability !== 'capable') {
		return undefined;
	}

	// oxlint-disable-next-line @typescript-eslint/consistent-type-assertions
	return SUPPORTER_VOICES_R2_ARMS.has(variation) ? (variation as SupporterVoicesR2Arm) : undefined;
}

/**
 * Whether V3's static-faces ask should be showing right now.
 *
 * Gated the same way as the invite it replaces: only on the arm's landing step (`uploadStep ===
 * 'ready'`), only while the video ask is the surface being shown (`shouldDisplaySupporterVoicesVideo`),
 * and never after the comment step's success screen (`shareSupporterVoiceSuccess`), which swaps the
 * whole flow over to `PostCommentWelcome`.
 */
export function useIsStaticFacesAskVisible(): boolean {
	const r2Arm = useSupporterVoicesR2Arm();
	const { uploadStep } = useUploadStepContext();
	const [{ shouldDisplaySupporterVoicesVideo, shareSupporterVoiceSuccess }] = usePetitionActionsPageState();

	return (
		r2Arm === 'v3_static_faces' &&
		uploadStep === 'ready' &&
		shouldDisplaySupporterVoicesVideo &&
		!shareSupporterVoiceSuccess
	);
}
